import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileDropzone } from "@/components/file-dropzone";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { FileSignature, Plus, Loader2, Calendar, AlertTriangle, Trash2, Bell, Archive, ArchiveRestore } from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";
import { formatCurrency } from "@/lib/utils";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Contract {
  id: number;
  title: string;
  referenceNumber: string | null;
  supplierName: string | null;
  endDate: string | null;
  startDate: string | null;
  cost: number | null;
  currency: string | null;
  renewalStatus: string;
  confirmed: boolean;
  originalFilename: string;
  archivedAt: string | null;
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    current: "bg-blue-100 text-blue-700",
    pending: "bg-slate-100 text-slate-700",
    review: "bg-amber-100 text-amber-700",
    renewed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700",
    "not-renewing": "bg-orange-100 text-orange-700",
  };
  const labels: Record<string, string> = {
    current: "Current contract",
    pending: "Pending review",
    review: "Under review",
    renewed: "Renewed",
    cancelled: "Cancelled",
    "not-renewing": "Not renewing",
  };
  return <span className={`text-xs px-2 py-1 rounded-full font-medium ${map[status] || "bg-slate-100 text-slate-700"}`}>{labels[status] || status}</span>;
}

function expiryBadge(endDate: string | null) {
  if (!endDate) return <span className="text-xs text-slate-400">No end date</span>;
  let days: number;
  try {
    days = differenceInDays(parseISO(endDate), new Date());
  } catch {
    return <span className="text-xs text-slate-400">{endDate}</span>;
  }
  if (days < 0) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Expired {Math.abs(days)}d ago</Badge>;
  if (days <= 7) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{days}d left</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{days}d left</Badge>;
  if (days <= 60) return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{days}d left</Badge>;
  return <span className="text-xs text-slate-500">{days}d left</span>;
}

export default function ContractsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState<"active" | "archived">("active");
  const isAdmin = user?.role === "admin";

  const { data: contracts = [], isLoading } = useQuery<Contract[]>({
    queryKey: ["contracts", view],
    queryFn: async () => {
      const qs = view === "archived" ? "?archived=true" : "";
      const res = await fetch(`${BASE}/api/contracts${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load contracts");
      return res.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BASE}/api/contracts/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast({ title: "Contract uploaded and analyzed" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/contracts/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast({ title: "Contract deleted" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: number; archive: boolean }) => {
      const res = await fetch(`${BASE}/api/contracts/${id}/${archive ? "archive" : "unarchive"}`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(archive ? "Failed to archive" : "Failed to restore");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      toast({ title: vars.archive ? "Contract archived" : "Contract restored to live" });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const checkAlertsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/contracts/check-alerts`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to check alerts");
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: `Sent ${data.sent} alert${data.sent === 1 ? "" : "s"}` });
    },
    onError: (err: any) => toast({ title: err.message, variant: "destructive" }),
  });

  const handleFiles = async (files: File[]) => {
    setUploading(true);
    for (const file of files) {
      try {
        await uploadMutation.mutateAsync(file);
      } catch {}
    }
    setUploading(false);
  };

  const handleDelete = (id: number, title: string) => {
    if (!confirm(`Delete contract "${title}"? This cannot be undone.`)) return;
    deleteMutation.mutate(id);
  };

  const handleArchive = (id: number, title: string, archive: boolean) => {
    if (archive && !confirm(`Archive contract "${title}"? You can restore it later.`)) return;
    archiveMutation.mutate({ id, archive });
  };

  const sorted = [...contracts].sort((a, b) => {
    if (!a.endDate && !b.endDate) return 0;
    if (!a.endDate) return 1;
    if (!b.endDate) return -1;
    return a.endDate.localeCompare(b.endDate);
  });

  const expiringSoon = sorted.filter((c) => {
    if (!c.endDate) return false;
    try {
      const days = differenceInDays(parseISO(c.endDate), new Date());
      return days >= 0 && days <= 60;
    } catch {
      return false;
    }
  });
  const expired = sorted.filter((c) => {
    if (!c.endDate) return false;
    try {
      return differenceInDays(parseISO(c.endDate), new Date()) < 0;
    } catch {
      return false;
    }
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-duffells-grey flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-primary" />
              Contracts
            </h1>
            <p className="text-sm text-duffells-grey/60 mt-1">
              Upload supplier contracts. Key dates and details are extracted automatically.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => checkAlertsMutation.mutate()}
            disabled={checkAlertsMutation.isPending}
          >
            {checkAlertsMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Check Renewal Alerts
          </Button>
        </div>

        <div className="flex items-center gap-2 border-b border-slate-200">
          <button
            onClick={() => setView("active")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              view === "active"
                ? "border-primary text-primary"
                : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            Live
          </button>
          <button
            onClick={() => setView("archived")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
              view === "archived"
                ? "border-primary text-primary"
                : "border-transparent text-duffells-grey/60 hover:text-duffells-grey"
            }`}
          >
            <Archive className="w-4 h-4" />
            Archived
          </button>
        </div>

        {view === "active" && (expiringSoon.length > 0 || expired.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expired.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <h3 className="font-semibold text-red-900">Expired ({expired.length})</h3>
                </div>
                <ul className="text-sm space-y-1">
                  {expired.slice(0, 5).map((c) => (
                    <li key={c.id} className="truncate">
                      <button onClick={() => navigate(`/contracts/${c.id}`)} className="text-red-700 hover:underline">
                        {c.title}
                      </button>{" "}
                      <span className="text-red-600/70">— ended {c.endDate}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {expiringSoon.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-900">Expiring soon ({expiringSoon.length})</h3>
                </div>
                <ul className="text-sm space-y-1">
                  {expiringSoon.slice(0, 5).map((c) => (
                    <li key={c.id} className="truncate">
                      <button onClick={() => navigate(`/contracts/${c.id}`)} className="text-amber-800 hover:underline">
                        {c.title}
                      </button>{" "}
                      <span className="text-amber-700/70">— {c.endDate}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {view === "active" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="font-semibold text-duffells-grey mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Upload Contract PDF
            </h2>
            <FileDropzone
              onUpload={handleFiles}
              accept=".pdf"
              label="Drop contract PDF here"
              multiple={true}
              maxSize={50 * 1024 * 1024}
            />
            {uploading && (
              <div className="mt-3 flex items-center gap-2 text-sm text-duffells-grey">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing contract with AI…
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-12 text-center text-duffells-grey/60">
              <FileSignature className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p>{view === "archived" ? "No archived contracts" : "No contracts uploaded yet"}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs font-medium text-duffells-grey/70 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Title</th>
                  <th className="px-4 py-3 text-left">Supplier</th>
                  <th className="px-4 py-3 text-left">End Date</th>
                  <th className="px-4 py-3 text-left">Expiry</th>
                  <th className="px-4 py-3 text-right">Cost</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/contracts/${c.id}`)}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-duffells-grey">{c.title}</div>
                      {c.referenceNumber && <div className="text-xs text-duffells-grey/60">Ref: {c.referenceNumber}</div>}
                      {!c.confirmed && <div className="text-xs text-amber-600 mt-0.5">Needs review</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-duffells-grey">{c.supplierName || "—"}</td>
                    <td className="px-4 py-3 text-sm text-duffells-grey">
                      {c.endDate ? (() => { try { return format(parseISO(c.endDate), "d MMM yyyy"); } catch { return c.endDate; } })() : "—"}
                    </td>
                    <td className="px-4 py-3">{expiryBadge(c.endDate)}</td>
                    <td className="px-4 py-3 text-right text-sm text-duffells-grey">
                      {c.cost != null ? formatCurrency(c.cost, c.currency || "GBP") : "—"}
                    </td>
                    <td className="px-4 py-3">{statusBadge(c.renewalStatus)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        {c.archivedAt ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleArchive(c.id, c.title, false); }}
                            className="text-slate-500 hover:text-primary inline-flex items-center gap-1 text-xs"
                            title="Restore to live"
                          >
                            <ArchiveRestore className="w-4 h-4" />
                            Restore
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleArchive(c.id, c.title, true); }}
                            className="text-slate-500 hover:text-amber-600"
                            title="Archive contract"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(c.id, c.title); }}
                            className="text-red-500 hover:text-red-700"
                            title="Delete permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
